import { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Card, CardContent, Divider, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Vehicle } from '../../types/vehicle';
import vehicleService from '../../services/vehicleService';
import DriverFooter from '../../components/driver/DriverFooter';

export const DriverDashboard = () => {
  const { t: td } = useTranslation('driver'); // Traductions du module driver
  const { t } = useTranslation(); // Traductions globales
  const { user } = useAuth();
  const [assignedVehicle, setAssignedVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAssignedVehicle = async () => {
      if (!user?.id || !user.organizationId) {
        console.error('Missing user information:', { user });
        setError(td('errors.userInfoMissing'));
        setLoading(false);
        return;
      }

      // Attendre un peu pour laisser le temps aux données d'être mises à jour
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      try {
        console.log('Loading assigned vehicle for user:', { 
          userId: user.id, 
          organizationId: user.organizationId 
        });
        
        const vehicle = await vehicleService.getAssignedVehicle(
          user.organizationId,
          user.id
        );
        console.log('Assigned vehicle result:', vehicle);
        
        setAssignedVehicle(vehicle);
        setError(null);
      } catch (error) {
        console.error('Error loading assigned vehicle:', error);
        setError(td('errors.loadVehicleFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadAssignedVehicle();
  }, [user, td]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <DriverFooter />
      </Box>
    );
  }

  if (!assignedVehicle) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {td('noVehicleAssigned')}
        </Alert>
        <DriverFooter />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 7 }}> {/* Padding bottom pour le footer */}
      {/* En-tête */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {td('welcome', { name: user?.firstName })}
        </Typography>
      </Paper>

      {/* Carte du véhicule */}
      <Card sx={{ mx: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {td('assignedVehicle')}
          </Typography>
          <Typography variant="body1">
            {assignedVehicle.make} {assignedVehicle.model} ({assignedVehicle.year})
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {assignedVehicle.licensePlate}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2">
            {td('vehicle.currentMileage')}: {assignedVehicle.currentMileage.toLocaleString()} km
          </Typography>
          <Typography variant="body2">
            {td('vehicle.fuelType')}: {td(`vehicle.fuelTypes.${assignedVehicle.fuelType}`)}
          </Typography>
          {assignedVehicle.lastMileageUpdate && (
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              {td('vehicle.lastUpdate')}: {assignedVehicle.lastMileageUpdate.toDate().toLocaleDateString()}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Dernières activités */}
      <Card sx={{ mx: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {td('lastActivities')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {td('noRecentActivities')}
          </Typography>
        </CardContent>
      </Card>

      {/* Alertes */}
      <Card sx={{ mx: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {td('alerts')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {td('noActiveAlerts')}
          </Typography>
        </CardContent>
      </Card>

      {/* Footer de navigation */}
      <DriverFooter />
    </Box>
  );
};

export default DriverDashboard;
